/* =====================================================================
 * perfMetrics — 핵심 UX 구간 성능 계측 유틸
 * ===================================================================== */

export type PerfMetricDetail = {
  name: string;
  durationMs?: number;
  ts: number;
  meta?: Record<string, unknown>;
};

export const PERF_EVENT = 'app:perfMetric';
const pendingHistoryClicks = new Map<string, string>();

function emitMetric(name: string, durationMs?: number, meta?: Record<string, unknown>): void {
  const detail: PerfMetricDetail = {
    name,
    ...(typeof durationMs === 'number' ? { durationMs } : {}),
    ts: Date.now(),
    ...(meta ? { meta } : {}),
  };

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<PerfMetricDetail>(PERF_EVENT, { detail }));
  }
}

function safeMeasure(
  measureName: string,
  startMark: string,
  endMark: string,
  meta?: Record<string, unknown>,
  options?: {
    clearStartMark?: boolean;
    clearEndMark?: boolean;
  },
): void {
  try {
    performance.measure(measureName, startMark, endMark);
    const entries = performance.getEntriesByName(measureName, 'measure');
    const latest = entries[entries.length - 1];
    emitMetric(measureName, latest?.duration, meta);
    performance.clearMeasures(measureName);
  } catch {
    emitMetric(`${measureName}:fallback`, undefined, meta);
  } finally {
    if (options?.clearStartMark ?? true) {
      performance.clearMarks(startMark);
    }
    if (options?.clearEndMark ?? true) {
      performance.clearMarks(endMark);
    }
  }
}

export function markLoginStart(): void {
  performance.mark('perf:loginStart');
}

export function markAuthReady(meta?: Record<string, unknown>): void {
  const endMark = 'perf:authReady';
  performance.mark(endMark);
  safeMeasure(
    'loginStart->authReady',
    'perf:loginStart',
    endMark,
    meta,
    {
      // 체인 계측을 위해 mark를 유지하고, 마지막 구간에서 정리합니다.
      clearStartMark: false,
      clearEndMark: false,
    },
  );
}

export function markWorkspaceBootstrapStart(): void {
  performance.mark('perf:workspaceBootstrapStart');
}

export function markWorkspaceBootstrapDone(): void {
  const endMark = 'perf:workspaceBootstrapDone';
  performance.mark(endMark);
  safeMeasure(
    'authReady->workspaceBootstrapDone',
    'perf:authReady',
    endMark,
    undefined,
    {
      clearStartMark: true,
      clearEndMark: false,
    },
  );
  safeMeasure(
    'workspaceBootstrapStart->workspaceBootstrapDone',
    'perf:workspaceBootstrapStart',
    endMark,
  );
}

export function markFirstInteractiveWorkspace(): void {
  const endMark = 'perf:firstInteractiveWorkspace';
  performance.mark(endMark);
  safeMeasure(
    'loginStart->firstInteractiveWorkspace',
    'perf:loginStart',
    endMark,
  );
}

export function markHistoryItemClick(conversationId: string): void {
  if (!conversationId) return;
  const markName = `perf:historyClick:${conversationId}`;
  pendingHistoryClicks.set(conversationId, markName);
  performance.mark(markName);
}

export function markFirstMessagePaint(conversationId: string, messageCount: number): void {
  const startMark = pendingHistoryClicks.get(conversationId);
  if (!startMark) return;
  pendingHistoryClicks.delete(conversationId);

  const endMark = `perf:firstMessagePaint:${conversationId}`;
  performance.mark(endMark);
  safeMeasure(
    'historyItemClick->firstMessagePaint',
    startMark,
    endMark,
    { conversationId, messageCount },
  );
}

export function trackWorkspaceBootstrapCache(
  status: 'hit' | 'miss' | 'write',
  meta?: Record<string, unknown>,
): void {
  emitMetric('workspaceBootstrapCache', undefined, { status, ...(meta ?? {}) });
}

let staleLoadingGuardCount = 0;
export function trackStaleLoadingGuard(): void {
  staleLoadingGuardCount += 1;
  emitMetric('staleLoadingGuardHit', undefined, { count: staleLoadingGuardCount });
}

